import { useMutation, useQueryClient, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import {
  VatPeriodDataResponse,
  VatPeriodDataPost,
  InputVATModule,
  OutputVATModule,
  Setup,
  VAT_TYPE,
  VatAllowedTypes,
  VatTotalsResponse,
  VatTotalResultsResponse,
  VatBaseFieldsKeys
  // VatTotalsResponse,
} from '../types'
import { PeriodType, PeriodTypeQuery, PeriodTypeView } from '@/types'
import { axiosInstance } from '@/api/axios'
import { createQueryKey, FINANCIAL_MODEL_SETTINGS_BASE_KEY, invalidateGlobalsQueryKey, VAT_BASE_KEY } from '@/api/query-keys'

const VAT_SETUP_KEY = createQueryKey({
  baseKey: VAT_BASE_KEY,
  keys: [`setup`]
})

const VAT_PERIOD_DATA_KEY = createQueryKey({
  baseKey: VAT_BASE_KEY,
  keys: [`period-data`]
})

const VAT_TOTALS_DATA_KEY = createQueryKey({
  baseKey: VAT_BASE_KEY,
  keys: [`totals-data`]
})

const getVatSetup = async (financialModelId: string) => {
  const { data } = await axiosInstance.get<Setup>(`/financial-models/${financialModelId}/vat/setup`)
  return data
}

export const useGetVatSetup = (financialModelId: string) =>
  useSuspenseQuery({
    queryKey: VAT_SETUP_KEY.concat([financialModelId]),
    queryFn: () => getVatSetup(financialModelId)
  })

const updateVatSetup = async (financialModelId: string, data: Setup) => {
  const { data: response } = await axiosInstance.post<Setup>(`/financial-models/${financialModelId}/vat/setup`, data)
  return response
}

export const useUpdateVatSetup = (financialModelId: string) =>
  useMutation({
    mutationFn: (data: Setup) => updateVatSetup(financialModelId, data),
    onSuccess: () => {
      invalidateGlobalsQueryKey([VAT_BASE_KEY, FINANCIAL_MODEL_SETTINGS_BASE_KEY])
    }
  })

interface GetVatPeriodDataParams {
  financialModelId: string
  periodType: PeriodTypeQuery
  vat_type: VAT_TYPE
  modules: InputVATModule[] | OutputVATModule[]
  view: PeriodTypeView
}

const getVatPeriodData = async (params: GetVatPeriodDataParams): Promise<VatPeriodDataResponse[]> => {
  const promises = params.modules.map(module =>
    axiosInstance.get<VatPeriodDataResponse>(`/financial-models/${params.financialModelId}/vat/data-entry/${module}`, {
      params: {
        type: params.periodType,
        vat_type: params.vat_type,
        view: params.view
      }
    })
  )
  const data = await Promise.all(promises)
  return data.map(item => item.data)
}

export const useGetVatPeriodData = ({ financialModelId, periodType, vat_type, modules, view }: GetVatPeriodDataParams) =>
  useSuspenseQuery({
    queryKey: VAT_PERIOD_DATA_KEY.concat([financialModelId, vat_type, periodType, view]),
    queryFn: async () =>
      getVatPeriodData({
        financialModelId,
        periodType,
        vat_type,
        modules,
        view
      })
  })

export const useGetVatPeriodDataQuery = ({ financialModelId, periodType, vat_type, modules, view }: GetVatPeriodDataParams) =>
  useQuery({
    queryKey: VAT_PERIOD_DATA_KEY.concat([financialModelId, vat_type, periodType, view, modules.join(`,`)]),
    queryFn: () =>
      getVatPeriodData({
        financialModelId,
        periodType,
        vat_type,
        modules,
        view
      }),
    enabled: Boolean(financialModelId && vat_type)
  })

export const updateVatPeriodData = async (
  financialModelId: string,
  module: InputVATModule | OutputVATModule,
  vat_type: VAT_TYPE,
  payload: VatPeriodDataPost
): Promise<VatPeriodDataResponse> => {
  const { data } = await axiosInstance.post<VatPeriodDataResponse>(`/financial-models/${financialModelId}/vat/data-entry/${module}`, payload, {
    params: {
      vat_type
    }
  })
  return data
}

type UpdateVatPeriodDataParams = {
  financialModelId: string
  module: InputVATModule | OutputVATModule
  vat_type: VAT_TYPE
  payload: VatPeriodDataPost
}

export const useUpdateVatPeriodData = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: UpdateVatPeriodDataParams) => updateVatPeriodData(financialModelId, params.module, params.vat_type, params.payload),
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({
        queryKey: VAT_PERIOD_DATA_KEY.concat([financialModelId, data.vat_type])
      })
      queryClient.invalidateQueries({
        queryKey: VAT_TOTALS_DATA_KEY
      })
    }
  })
}

type SwitchVatColumnTypeParams = {
  financialModelId: string
  input_type: VatAllowedTypes
  module: InputVATModule | OutputVATModule
  column_name: VatBaseFieldsKeys
  vat_type: VAT_TYPE
  month?: number
  year?: number
}

const switchVatColumnType = async (params: SwitchVatColumnTypeParams) => {
  const { data } = await axiosInstance.post<VatPeriodDataResponse>(
    `/financial-models/${params.financialModelId}/vat/config/${params.column_name}`,
    undefined,
    {
      params: {
        input_type: params.input_type,
        module: params.module,
        month: params.month,
        year: params.year,
        vat_type: params.vat_type
      }
    }
  )
  return data
}

type SwitchVatQueryColumnTypeParams = Omit<SwitchVatColumnTypeParams, 'financialModelId'>

export const useSwitchVatColumnType = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (params: SwitchVatQueryColumnTypeParams) => switchVatColumnType({ ...params, financialModelId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: VAT_PERIOD_DATA_KEY.concat([financialModelId])
      })
    }
  })
}

const getVatTotalsData = async (financialModelId: string, periodType: PeriodType, vat_type: VAT_TYPE, view: PeriodTypeView) => {
  const { data } = await axiosInstance.get<VatTotalsResponse>(`/financial-models/${financialModelId}/vat/data-entry/${vat_type}/totals`, {
    params: {
      type: periodType,
      view
    }
  })
  return data
}

type GetVatTotalsDataParams = {
  financialModelId: string
  periodType: PeriodType
  vat_type: VAT_TYPE
  view: PeriodTypeView
}

export const useGetVatTotalsData = ({ financialModelId, periodType, vat_type, view }: GetVatTotalsDataParams) =>
  useSuspenseQuery({
    queryKey: VAT_TOTALS_DATA_KEY.concat([financialModelId, vat_type, periodType, view]),
    queryFn: () => getVatTotalsData(financialModelId, periodType, vat_type, view)
  })

const getVatTotalResultsData = async (financialModelId: string, periodType: PeriodTypeQuery, view: PeriodTypeView) => {
  const { data } = await axiosInstance.get<VatTotalResultsResponse>(`/financial-models/${financialModelId}/vat/data-entry/totals`, {
    params: {
      type: periodType,
      view
    }
  })
  return data
}

type GetVatTotalResultsDataParams = {
  financialModelId: string
  periodType: PeriodTypeQuery
  view: PeriodTypeView
}

export const useGetVatTotalResultsData = ({ financialModelId, periodType, view }: GetVatTotalResultsDataParams) =>
  useSuspenseQuery({
    queryKey: VAT_TOTALS_DATA_KEY.concat([financialModelId, periodType, view]),
    queryFn: () => getVatTotalResultsData(financialModelId, periodType, view)
  })
